import { Injectable, Inject, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { KeycloakAuthService } from '../core/services/keycloak-auth.service';
import { ApiConfigService } from '../core/services/api-config.service';

export interface FlowExecutionLog {
  Id: string;
  ProcessId: string;
  ExternalId: string;
  IntegrationId: string;
  CreatedAt: string;
  LastUpdatedAt: string;
  StepsTotal: number;
  State: FlowExecutionStatus;
  LastStepName: string;
  NextStepName: string;
  StepsCompleted: number;
  OutputMessage: string;
  Output: Record<string, any>;
  Logs: LogItem[];
  Warnings: LogItem[];
  Faults: LogItem[];
}

export interface FlowActivitiesExecutionLog {
  Id: string;
  ExternalId: string | null;
  StatusMessage: string | null;
  CurrentActivityExecutionId: string | null;
  AuditTrail: ActivityAuditTrail[];
}

export interface ActivityAuditTrail {
  ActivityId: string | null;
  ActivityExternalId: string | null;
  ActivityName: string | null;
  ActivityExecutionId: string;
  DisplayStatus: string | null;
  ExecutionStatus: FlowExecutionStatus;
  InputData: Record<string, any> | null;
  OutputMessage: string | null;
  Output: Record<string, any> | null;
}

export interface LogItem {
  StepId: string;
  Message: string;
  InnerMessage: string;
}

export enum FlowExecutionStatus {
  Created = "Created",
  Running = "Running",
  Completed = "Completed",
  Faulted = "Faulted",
  Canceled = "Canceled"
}

@Injectable({
  providedIn: 'root'
})
export class FlowExecutionService {
  private authService = inject(KeycloakAuthService);
  private apiConfig = inject(ApiConfigService);
  private api: AxiosInstance;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    this.api = axios.create({
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Add request interceptor to dynamically set baseURL and include OAuth token
    this.api.interceptors.request.use((config) => {
      // Dynamically construct baseURL on each request to get current tenantId
      if (isPlatformBrowser(this.platformId)) {
        const baseUrl = this.apiConfig.getApiBaseUrl();
        const tenantId = this.apiConfig.getTenantId();
        config.baseURL = `${baseUrl}/v2/${tenantId}`;

      }

      const token = this.authService.accessToken;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }


  async executeFlow(
    integrationId: string,
    integratedFlow: string | null,
    externalId: string | null,
    inputData: Record<string, any>,
    files?: Record<string, string>,
    dataContextId?: string | null
  ): Promise<FlowExecutionLog | null> {
    try {
      let endpoint = integrationId + "/flow/execute";
      let body: {
        InputData: Record<string, any>;
        ExternalId?: string | null;
        Files?: Record<string, string>;
        DataContextId?: string;
      } = {
        InputData: inputData
      };

      if (files) {
        body.Files = files;
      }

      if (dataContextId) {
        body.DataContextId = dataContextId;
      }

      if (integratedFlow) {
        endpoint += "/" + integratedFlow;
      }
      if (externalId) {
        body.ExternalId = externalId;
      }

      const response = await this.api.post(endpoint, body);
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async getFlowExecutionLog(id: string, integrationId: string): Promise<FlowExecutionLog | null> {
    try {
      let endpoint = integrationId + "/flow/" + id;
      const response = await this.api.get(endpoint);
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async getFlowExecutionLogByExternalId(externalId: string, integrationId: string): Promise<FlowExecutionLog[] | null> {
    try {
      let endpoint = integrationId + "/flow/external/" + externalId;
      const response = await this.api.get(endpoint);
      let data = response.data as FlowExecutionLog[];
      data.sort((a, b) => new Date(b.CreatedAt).getTime() - new Date(a.CreatedAt).getTime());
      return data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }
}
